// src/hooks/usePaymentsPage.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';

/**
 * Server-side Pagination Hook for Payments (via rism.payments_list_v)
 *
 * - Queries the view so OR search is a simple "invoice_number OR student_name"
 * - Uses count: 'exact' for totals and range(from,to) for paging
 * - Excludes is_deleted = true
 */
export function usePaymentsPage({ page, pageSize, search = '', status = 'All' }) {
  const qc = useQueryClient();
  const from = Math.max(0, (Number(page) - 1) * Number(pageSize));
  const to = from + Number(pageSize) - 1;

  const queryKey = ['payments_page', { page, pageSize, search, status }];

  const queryFn = async () => {
    const hasSearch = Boolean(search?.trim());
    const raw = (search || '').trim();
    // Light sanitize so logic-tree isn't broken by commas/parens
    const safe = raw.replaceAll(',', ' ').replaceAll('(', ' ').replaceAll(')', ' ').trim();

    let query = supabase
      .from('payments_list_v') // <-- the view
      .select(
        `
        id,
        student_id,
        student_name,
        invoice_number,
        amount,
        payment_method,
        payment_date,
        status,
        payment_type,
        created_at,
        is_deleted
      `,
        { count: 'exact' }
      )
      .eq('is_deleted', false);

    if (status && status !== 'All') {
      query = query.eq('status', status);
    }

    if (hasSearch) {
      // Logic-tree OR across two plain columns = safe and stable
      const pattern = `*${safe}*`;
      query = query.or(`invoice_number.ilike.${pattern},student_name.ilike.${pattern}`);
    }

    query = query
      .order('payment_date', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .range(from, to);

    const { data, count, error } = await query;
    if (error) throw error;

    // Shape matches your component expectations
    const rows = (data || []).map((r) => ({
      ...r,
      // keep student_name field as used by your UI
    }));

    return { rows, count, page, pageSize };
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn,
    keepPreviousData: true,
    staleTime: 30_000,
  });

  // Soft delete still updates the base table
  const { mutateAsync: softDeletePayment } = useMutation({
    mutationFn: async ({ id }) => {
      if (!id) throw new Error('Missing payment id');
      const { error } = await supabase.from('payments').update({ is_deleted: true }).eq('id', id);
      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payments_page'] });
    },
  });

  return {
    rows: data?.rows ?? [],
    count: data?.count ?? 0,
    page,
    pageSize,
    isLoading,
    error: error?.message || null,
    refetch,
    softDeletePayment,
  };
}
